<template>
  <div id="contenedor-perfil-usuario">
    <template v-if="soloImagen && !$apollo.loading">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-avatar
            v-bind="attrs"
            v-on="on"
            :color="getDatosUsuario.avatarPublicKey ? '' : 'primary'"
            size="40">
            <v-img
              v-if="getDatosUsuario.avatarPublicKey"
              :src="`${$urlFileServer}/${getDatosUsuario.avatarPublicKey}`"></v-img>
            <span v-else class="white--text text-subtitle-2">
              {{ getIniciales(getDatosUsuario.funcionario.nombre) }}
            </span>
          </v-avatar>
        </template>
        <span>{{ getDatosUsuario.funcionario.nombre }}</span>
      </v-tooltip>
    </template>
    <template v-if="soloInfo && !$apollo.loading">
      <v-row justify="center">
        <v-avatar
          :color="getDatosUsuario.avatarPublicKey ? '' : 'primary'"
          size="90"
          class="elevation-4">
          <v-img
            v-if="getDatosUsuario.avatarPublicKey"
            :src="`${$urlFileServer}/${getDatosUsuario.avatarPublicKey}`"></v-img>
          <span v-else class="white--text text-h4">
            {{ getIniciales(getDatosUsuario.funcionario.nombre) }}
          </span>
        </v-avatar>
      </v-row>
      <v-row class="mt-2" justify="center">
        <h3>{{ getDatosUsuario.funcionario.nombre }}</h3>
      </v-row>
      <v-row class="mb-4" justify="center">
        <span class="grey--text">{{ getDatosUsuario.funcionario.puesto }}</span>
      </v-row>
    </template>
    <template v-if="!soloInfo && !soloImagen">
      <v-skeleton-loader
        v-if="$apollo.loading"
        type="card-avatar, article, actions"
      ></v-skeleton-loader>

      <div v-if="!$apollo.loading">
        <!-- Header con Avatar -->
        <v-card flat class="mb-4">
          <v-card-text class="text-center pb-0">
            <v-avatar
              :color="getDatosUsuario.avatarPublicKey ? '' : 'primary'"
              size="120"
              class="mb-4 elevation-4">
              <v-img
                v-if="getDatosUsuario.avatarPublicKey"
                :src="`${$urlFileServer}/${getDatosUsuario.avatarPublicKey}`"
                :alt="getDatosUsuario.funcionario.nombre"></v-img>
              <span v-else class="white--text text-h3 font-weight-medium">
                {{ getIniciales(getDatosUsuario.funcionario.nombre) }}
              </span>
            </v-avatar>
            <h2 class="text-h4 font-weight-bold mb-2">
              {{ getDatosUsuario.funcionario.nombre }}
            </h2>
            <p class="text-h6 grey--text mb-2">
              {{ getDatosUsuario.funcionario.puesto }}
            </p>
            <v-chip
              :color="getDatosUsuario.estatus === 1 ? 'success' : 'grey'"
              small
              class="mb-4">
              <v-icon left small>
                {{ getDatosUsuario.estatus === 1 ? 'mdi-check-circle' : 'mdi-cancel' }}
              </v-icon>
              {{ getDatosUsuario.estatus === 1 ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </v-card-text>

          <!-- Botones de Acción -->
          <v-card-actions class="justify-center pb-4">
            <v-btn
              color="primary"
              outlined
              @click="mostarActualizarDatos = true">
              <v-icon left>mdi-account-edit</v-icon>
              Editar Perfil
            </v-btn>
            <v-btn
              color="primary"
              outlined
              @click="mostrarAgregarImagen = true">
              <v-icon left>mdi-camera</v-icon>
              {{ getDatosUsuario.avatarPublicKey ? 'Cambiar' : 'Agregar' }} Foto
            </v-btn>
            <v-btn
              color="primary"
              outlined
              @click="mostrarCambiarPassword = true">
              <v-icon left>mdi-key-variant</v-icon>
              Cambiar Contraseña
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-row>
          <!-- Información Personal -->
          <v-col cols="12" md="6">
            <v-card flat outlined>
              <v-card-title class="primary--text">
                <v-icon left color="primary">mdi-account-details</v-icon>
                Información Personal
              </v-card-title>
              <v-divider></v-divider>
              <v-list>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-email</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle>Correo Electrónico</v-list-item-subtitle>
                    <v-list-item-title>{{ getDatosUsuario.correoElectronico }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="mostrarCambiarCorreo = true">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-calendar</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle>Miembro Desde</v-list-item-subtitle>
                    <v-list-item-title>
                      {{ formatDate(getDatosUsuario.createdAt) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon
                      :class="getDatosUsuario.funcionario.esSupervisor
                      ? 'amber--text text--darken-2'
                      : ''">
                      fa-hard-hat
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle>Supervisor de Obra</v-list-item-subtitle>
                    <v-list-item-title>
                      {{ getDatosUsuario.funcionario.esSupervisor ? 'Sí' : 'No' }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

          <!-- Roles y Permisos -->
          <v-col cols="12" md="6">
            <v-card flat outlined>
              <v-card-title class="primary--text">
                <v-icon left color="primary">mdi-shield-account</v-icon>
                Roles y Permisos
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div class="mb-4">
                  <p class="text-subtitle-2 grey--text mb-2">Roles Asignados</p>
                  <div v-if="getDatosUsuario.roles && getDatosUsuario.roles.length > 0">
                    <v-chip
                      v-for="(rol, index) in getDatosUsuario.roles"
                      :key="index"
                      :color="getRolColor(rol)"
                      class="mr-2 mb-2"
                      label>
                      <v-icon left small>mdi-shield-check</v-icon>
                      {{ getRolLabel(rol) }}
                    </v-chip>
                  </div>
                  <p v-else class="grey--text text--lighten-1">Sin roles asignados</p>
                </div>

                <div v-if="getDatosUsuario.scopes && getDatosUsuario.scopes.length > 0">
                  <p class="text-subtitle-2 grey--text mb-2">
                    Permisos ({{ getDatosUsuario.scopes.length }})
                  </p>
                  <v-chip
                    v-for="(scope, index) in getDatosUsuario.scopes.slice(0, 6)"
                    :key="index"
                    x-small
                    outlined
                    class="mr-1 mb-1">
                    {{ scope }}
                  </v-chip>
                  <v-tooltip bottom v-if="getDatosUsuario.scopes.length > 6">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        x-small
                        v-bind="attrs"
                        v-on="on"
                        class="mb-1">
                        +{{ getDatosUsuario.scopes.length - 6 }} más
                      </v-chip>
                    </template>
                    <div>
                      <div v-for="(scope, index) in getDatosUsuario.scopes.slice(6)" :key="index">
                        {{ scope }}
                      </div>
                    </div>
                  </v-tooltip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Comentarios (si existen) -->
        <v-row v-if="getDatosUsuario.comentarios">
          <v-col cols="12">
            <v-card flat outlined>
              <v-card-title class="primary--text">
                <v-icon left color="primary">mdi-comment-text</v-icon>
                Comentarios
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <p>{{ getDatosUsuario.comentarios }}</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </template>
    <!-- Ventana para cambiar contraseña -->
    <v-dialog
      v-model="mostrarCambiarPassword"
      width="500">
      <cambiar-password
      v-if="mostrarCambiarPassword"
      :usuarioId="usuarioId"
      v-on:cerrar="cerrarVentana">
      </cambiar-password>
    </v-dialog>
    <!-- Ventana para actualizar correo electronico -->
    <v-dialog
      v-model="mostrarCambiarCorreo"
      width="500">
      <cambiar-correo
      v-if="mostrarCambiarCorreo"
      :usuarioId="usuarioId"
      v-on:cerrar="cerrarVentana">
      </cambiar-correo>
    </v-dialog>
    <!-- Ventana para actualizar datos del usuario -->
    <v-dialog
      v-model="mostarActualizarDatos"
      width="500">
      <actualizar-datos
      v-if="mostarActualizarDatos"
      :funcionarioId="getDatosUsuario.funcionario.id"
      v-on:cerrar="cerrarVentana">
      </actualizar-datos>
    </v-dialog>
    <!-- Ventana para agregar imagen de perfil a usuario -->
    <v-dialog
      v-model="mostrarAgregarImagen"
      width="500">
      <agregar-imagen
      v-if="mostrarAgregarImagen"
      :usuarioId="usuarioId"
      :datosUsuario="getDatosUsuario"
      v-on:cerrar="cerrarVentana">
      </agregar-imagen>
    </v-dialog>
  </div>
</template>

<script>
import USUARIO from '@/graphql/querys/administracion/catalogos/usuarios/usuario.gql';
import ActualizarDatos from '@/components/administracion/configuracion/catalogos/usuarios/ActualizarDatos.vue';
import AgregarImagen from '@/components/administracion/configuracion/catalogos/usuarios/AgregarImagen.vue';
import CambiarPassword from './CambiarPassword.vue';
import CambiarCorreo from './CambiarCorreo.vue';

export default {
  name: 'PerfilUsuario',
  props: {
    usuarioId: {
      type: String,
      required: true,
    },
    soloInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    soloImagen: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    CambiarPassword,
    CambiarCorreo,
    ActualizarDatos,
    AgregarImagen,
  },
  apollo: {
    usuario: {
      query: USUARIO,
      variables() {
        return {
          id: this.usuarioId,
        };
      },
    },
  },
  data() {
    return {
      avatarPublicKey: null,
      mostrarCambiarPassword: false,
      mostrarCambiarCorreo: false,
      mostarActualizarDatos: false,
      mostrarAgregarImagen: false,
      mostrarMenuEditar: false,
    };
  },
  methods: {
    getIniciales(nombreCompleto) {
      if (!nombreCompleto) return '?';
      const palabras = nombreCompleto.trim().split(' ');
      if (palabras.length === 1) {
        return palabras[0].substring(0, 2).toUpperCase();
      }
      return (palabras[0].charAt(0) + palabras[palabras.length - 1].charAt(0)).toUpperCase();
    },
    getRolColor(rol) {
      const colores = {
        admin: 'error',
        supervisor: 'warning',
        operador: 'info',
        consulta: 'grey',
      };
      return colores[rol] || 'primary';
    },
    getRolLabel(rol) {
      const labels = {
        admin: 'Administrador',
        supervisor: 'Supervisor',
        operador: 'Operador',
        consulta: 'Consulta',
      };
      return labels[rol] || rol;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    cerrarVentana() {
      this.mostrarCambiarPassword = false;
      this.mostrarCambiarCorreo = false;
      this.mostarActualizarDatos = false;
      this.mostrarAgregarImagen = false;
    },
    cerrar() {
      this.$emit('cerrar');
    },
  },
  computed: {
    getDatosUsuario() {
      if (!this.usuario) return {};
      return this.usuario;
    },
  },

};
</script>

<style>

</style>
